import React, { useState, useEffect } from 'react';

export default function MissionComplete({
  mission, result, onReplay, onNextMission, onBackToMap, playSound, gameSettings = {}
}) {
  const [showStars, setShowStars] = useState(0);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const stars = result?.stars || 0;
  const breakdown = result?.star_breakdown || {};
  const reward = result?.reward_coins || 0;
  const isNewBest = result?.is_new_best;
  const newlyUnlocked = result?.newly_unlocked_areas || [];

  // Animate stars one by one
  useEffect(() => {
    const timers = [];
    for (let i = 1; i <= 3; i++) {
      timers.push(setTimeout(() => {
        setShowStars(i);
        if (i <= stars) playSound?.(i === 3 && stars === 3 ? 'victory' : 'star');
      }, 500 + i * 400));
    }
    timers.push(setTimeout(() => setShowBreakdown(true), 2200));
    return () => timers.forEach(clearTimeout);
  }, [stars, playSound]);

  return (
    <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-4 animate-fade-up">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Top accent */}
          <div className={`h-2 ${stars === 3 ? 'bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400' : 'bg-gradient-to-r from-[#C84B31] to-[#D4A843]'}`} />

          <div className="p-6 sm:p-8 text-center">
            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              {stars === 3 ? (gameSettings.mission_complete_perfect || 'Perfect!') : stars >= 2 ? (gameSettings.mission_complete_great || 'Great Job!') : (gameSettings.mission_complete_done || 'Mission Complete')}
            </h2>
            <p className="text-sm text-gray-500 mb-6">{mission?.name_english || 'Mission'}</p>

            {/* Stars */}
            <div className="flex justify-center gap-3 mb-6">
              {[1, 2, 3].map(i => (
                <span
                  key={i}
                  className={`text-5xl transition-all ${
                    showStars >= i
                      ? i <= stars
                        ? `animate-star-${i} star star-filled`
                        : `animate-star-${i} star star-empty`
                      : 'opacity-0'
                  }`}
                >
                  ★
                </span>
              ))}
            </div>

            {/* Breakdown */}
            {showBreakdown && (
              <div className="animate-fade-up space-y-4">
                {/* Star criteria */}
                <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2.5">
                  <div className="flex items-center gap-2">
                    <span className={`text-base ${breakdown.all_items ? 'text-green-500' : 'text-gray-300'}`}>
                      {breakdown.all_items ? '✓' : '✗'}
                    </span>
                    <span className={`text-sm ${breakdown.all_items ? 'text-gray-800' : 'text-gray-400'}`}>
                      {gameSettings.mission_complete_all_items || 'All items found'}
                    </span>
                    <span className={`ml-auto text-sm ${breakdown.all_items ? 'text-amber-500' : 'text-gray-300'}`}>★</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-base ${breakdown.under_budget ? 'text-green-500' : 'text-gray-300'}`}>
                      {breakdown.under_budget ? '✓' : '✗'}
                    </span>
                    <span className={`text-sm ${breakdown.under_budget ? 'text-gray-800' : 'text-gray-400'}`}>
                      {gameSettings.mission_complete_under_budget || 'Under budget'}
                    </span>
                    <span className={`ml-auto text-sm ${breakdown.under_budget ? 'text-amber-500' : 'text-gray-300'}`}>★</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-base ${breakdown.darija_phrases ? 'text-green-500' : 'text-gray-300'}`}>
                      {breakdown.darija_phrases ? '✓' : '✗'}
                    </span>
                    <span className={`text-sm ${breakdown.darija_phrases ? 'text-gray-800' : 'text-gray-400'}`}>
                      {gameSettings.mission_complete_used_darija || 'Used Darija phrases'}
                    </span>
                    <span className={`ml-auto text-sm ${breakdown.darija_phrases ? 'text-amber-500' : 'text-gray-300'}`}>★</span>
                  </div>
                </div>

                {/* Reward */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-amber-800">{gameSettings.mission_complete_reward || 'Reward earned'}</span>
                  <span className="text-lg font-bold text-amber-700">{gameSettings.icon_token || '🪙'} +{reward}</span>
                </div>

                {isNewBest && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-2.5 text-center">
                    <p className="text-xs text-green-700 font-semibold">{gameSettings.mission_complete_new_best || 'New personal best!'} ⬆</p>
                  </div>
                )}

                {/* Newly unlocked areas */}
                {newlyUnlocked.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
                    <p className="text-xs text-purple-700 font-semibold mb-1">{gameSettings.mission_complete_area_unlocked || 'New Area Unlocked!'}</p>
                    {newlyUnlocked.map(a => (
                      <p key={a.id} className="text-sm font-bold text-purple-900">{a.name}</p>
                    ))}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3 pt-2">
                  {stars < 3 && (
                    <button
                      onClick={onReplay}
                      className="flex-1 py-3 rounded-xl border-2 border-[#C84B31] text-[#C84B31] font-bold text-sm hover:bg-[#C84B31] hover:text-white transition-colors"
                    >
                      {gameSettings.mission_complete_replay || 'Replay for 3★'}
                    </button>
                  )}
                  <button
                    onClick={stars === 3 ? onNextMission : onBackToMap}
                    className="flex-1 py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
                  >
                    {stars === 3 ? (gameSettings.mission_complete_next || 'Next Mission →') : (gameSettings.mission_complete_back || 'Back to Missions')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
